import { Injectable, Type } from '@angular/core';
import {
  ColumnDataOptions,
  ColumnDataOptionsTruncation,
  ColumnDataType,
  ColumnDefinition,
  ColumnVisibility,
  unmergeColumnDataOptions,
} from './../../model/column-definition';
import { AllowSort } from './allow-sort';

export type ColumnOptions<TSource = any, TValue = any, TComponent = any> = {
  visibility?: ColumnVisibility;
  helpBuilder?: (source: TSource) => string;
  helpText?: string;
  allowSort?: AllowSort;
  component?: Type<TComponent>;
  comparer?: (a: TValue, b: TValue) => number;
  totalSum?: boolean;
  serverSideSortBinding?: string;
  options?: ColumnDataOptions;
};

export type ColumnOptionsWithType<TSource = any, TValue = any, TComponent = any> = ColumnOptions<TSource, TValue, TComponent> & {
  columnDataType?: ColumnDataType;
};

@Injectable()
export class ColumnDefinitionFactory {
  private _create<TSource = any, TValue = any, TComponent = any>(
    caption: string,
    binding: string,
    options: ColumnOptionsWithType<TSource, TValue, TComponent>
  ): ColumnDefinition<TSource, TValue, TComponent> {
    return new ColumnDefinition(
      caption,
      binding,
      options.columnDataType || ColumnDataType.text,
      options.component || null,
      options.visibility,
      options.totalSum,
      options.comparer,
      options.helpBuilder,
      options.helpText,
      options.allowSort,
      options.serverSideSortBinding,
      options.options
    );
  }

  create<TSource = any, TValue = any>(
    caption: string,
    binding: string,
    visibility?: ColumnVisibility,
    helpBuilder?: (source: TSource) => string,
    helpText?: string
  ): ColumnDefinition<TSource, TValue>;
  create<TSource = any, TValue = any>(caption: string, binding: string, options?: ColumnOptionsWithType): ColumnDefinition<TSource, TValue>;
  create<TSource = any, TValue = any>(
    caption: string,
    binding: string,
    visOrOpt?: ColumnVisibility | ColumnOptionsWithType,
    helpBuilder?: (source: TSource) => string,
    helpText?: string
  ): ColumnDefinition<TSource, TValue> {
    if (typeof visOrOpt === 'object') {
      return this._create<TSource>(caption, binding, visOrOpt);
    } else {
      return this._create<TSource, TValue>(caption, binding, { visibility: visOrOpt, helpBuilder: helpBuilder, helpText: helpText });
    }
  }

  createBoolean<TSource = any>(caption: string, binding: string, options?: ColumnOptions): ColumnDefinition<TSource>;
  createBoolean<TSource = any>(caption: string, binding: string, visibility?: ColumnVisibility): ColumnDefinition<TSource>;
  createBoolean<TSource = any>(caption: string, binding: string, visOrOpt?: ColumnOptionsWithType | ColumnVisibility) {
    let opt: ColumnOptionsWithType;
    if (typeof visOrOpt === 'object') {
      opt = visOrOpt;
    } else {
      opt = { visibility: visOrOpt };
    }
    opt.columnDataType = ColumnDataType.boolean;
    return this._create<TSource>(caption, binding, opt);
  }

  createComponentBased<TComponent, TSource = any, TValue = any>(
    caption: string,
    binding: string,
    component: Type<TComponent>,
    options?: Omit<ColumnOptions, 'totalSum' | 'component'>
  ): ColumnDefinition<TSource, TValue, TComponent>;
  createComponentBased<TComponent, TSource = any, TValue = any>(
    caption: string,
    binding: string,
    component: Type<TComponent>,
    visibility?: ColumnVisibility
  ): ColumnDefinition<TSource, TValue, TComponent>;
  createComponentBased<TComponent, TSource = any, TValue = any>(
    caption: string,
    binding: string,
    component: Type<TComponent>,
    visOrOpt?: ColumnOptionsWithType | ColumnVisibility
  ) {
    let opt: ColumnOptionsWithType;
    if (typeof visOrOpt === 'object') {
      opt = visOrOpt;
    } else {
      opt = { visibility: visOrOpt };
    }
    opt.columnDataType = ColumnDataType.component;
    opt.component = component;
    opt.totalSum = false;
    return this._create<TSource>(caption, binding, opt);
  }

  createDate<TSource = any>(caption: string, binding: string, options?: ColumnOptions): ColumnDefinition<TSource>;
  createDate<TSource = any>(caption: string, binding: string, visibility?: ColumnVisibility): ColumnDefinition<TSource>;
  createDate<TSource = any>(caption: string, binding: string, visOrOpt?: ColumnOptionsWithType | ColumnVisibility) {
    let opt: ColumnOptionsWithType;
    if (typeof visOrOpt === 'object') {
      opt = visOrOpt;
    } else {
      opt = { visibility: visOrOpt };
    }
    opt.columnDataType = ColumnDataType.date;
    return this._create<TSource>(caption, binding, opt);
  }

  createDateTime<TSource = any>(caption: string, binding: string, options?: ColumnOptions): ColumnDefinition<TSource>;
  createDateTime<TSource = any>(caption: string, binding: string, visibility?: ColumnVisibility): ColumnDefinition<TSource>;
  createDateTime<TSource = any>(caption: string, binding: string, visOrOpt?: ColumnOptionsWithType | ColumnVisibility) {
    let opt: ColumnOptionsWithType;
    if (typeof visOrOpt === 'object') {
      opt = visOrOpt;
    } else {
      opt = { visibility: visOrOpt };
    }
    opt.columnDataType = ColumnDataType.dateTime;
    return this._create<TSource>(caption, binding, opt);
  }

  createDecimal<TSource = any>(caption: string, binding: string, options?: ColumnOptions): ColumnDefinition<TSource>;
  createDecimal<TSource = any>(caption: string, binding: string, visibility?: ColumnVisibility): ColumnDefinition<TSource>;
  createDecimal<TSource = any>(caption: string, binding: string, visOrOpt?: ColumnOptionsWithType | ColumnVisibility) {
    let opt: ColumnOptionsWithType;
    if (typeof visOrOpt === 'object') {
      opt = visOrOpt;
    } else {
      opt = { visibility: visOrOpt };
    }
    opt.columnDataType = ColumnDataType.decimal;
    return this._create<TSource>(caption, binding, opt);
  }

  createMoney<TSource = any>(caption: string, binding: string, options?: ColumnOptions): ColumnDefinition<TSource>;
  createMoney<TSource = any>(caption: string, binding: string, visibility?: ColumnVisibility): ColumnDefinition<TSource>;
  createMoney<TSource = any>(caption: string, binding: string, visOrOpt?: ColumnOptionsWithType | ColumnVisibility) {
    let opt: ColumnOptionsWithType;
    if (typeof visOrOpt === 'object') {
      opt = visOrOpt;
    } else {
      opt = { visibility: visOrOpt };
    }
    opt.columnDataType = ColumnDataType.money;
    if (opt.totalSum === undefined) {
      opt.totalSum = true;
    }
    return this._create<TSource>(caption, binding, opt);
  }

  createMoneyOptional<TSource = any>(caption: string, binding: string, options?: ColumnOptions): ColumnDefinition<TSource>;

  createMoneyOptional<TSource = any>(caption: string, binding: string, visibility?: ColumnVisibility): ColumnDefinition<TSource>;

  createMoneyOptional<TSource = any>(
    caption: string,
    binding: string,
    visOrOpt?: ColumnOptionsWithType | ColumnVisibility
  ): ColumnDefinition<TSource> {
    let opt: ColumnOptionsWithType;
    if (typeof visOrOpt === 'object') {
      opt = visOrOpt;
    } else {
      opt = { visibility: visOrOpt };
    }
    opt.columnDataType = ColumnDataType.moneyOptional;
    if (opt.totalSum === undefined) {
      opt.totalSum = true;
    }
    return this._create<TSource>(caption, binding, opt);
  }

  createNumber<TSource = any>(caption: string, binding: string, options?: ColumnOptions): ColumnDefinition<TSource>;
  createNumber<TSource = any>(caption: string, binding: string, visibility?: ColumnVisibility): ColumnDefinition<TSource>;
  createNumber<TSource = any>(caption: string, binding: string, visOrOpt?: ColumnOptionsWithType | ColumnVisibility) {
    let opt: ColumnOptionsWithType;
    if (typeof visOrOpt === 'object') {
      opt = visOrOpt;
    } else {
      opt = { visibility: visOrOpt };
    }
    opt.columnDataType = ColumnDataType.number;
    return this._create<TSource>(caption, binding, opt);
  }

  createPhoneNumber<TSource = any>(caption: string, binding: string, options?: ColumnOptions): ColumnDefinition<TSource>;
  createPhoneNumber<TSource = any>(caption: string, binding: string, visibility?: ColumnVisibility): ColumnDefinition<TSource>;
  createPhoneNumber<TSource = any>(caption: string, binding: string, visOrOpt?: ColumnOptionsWithType | ColumnVisibility) {
    let opt: ColumnOptionsWithType;
    if (typeof visOrOpt === 'object') {
      opt = visOrOpt;
    } else {
      opt = { visibility: visOrOpt };
    }
    opt.columnDataType = ColumnDataType.phone;
    return this._create<TSource>(caption, binding, opt);
  }

  createTranslation<TSource = any>(caption: string, binding: string, options?: ColumnOptions): ColumnDefinition<TSource>;
  createTranslation<TSource = any>(caption: string, binding: string, visibility?: ColumnVisibility): ColumnDefinition<TSource>;
  createTranslation<TSource = any>(caption: string, binding: string, visOrOpt?: ColumnOptionsWithType | ColumnVisibility) {
    let opt: ColumnOptionsWithType;
    if (typeof visOrOpt === 'object') {
      opt = visOrOpt;
    } else {
      opt = { visibility: visOrOpt };
    }
    opt.columnDataType = ColumnDataType.translation;
    return this._create<TSource>(caption, binding, opt);
  }

  createTruncated<TSource = any>(
    caption: string,
    binding: string,
    options: ColumnOptions & ColumnDataOptionsTruncation
  ): ColumnDefinition<TSource> {
    const opt = unmergeColumnDataOptions(options);
    opt.columnDataType = ColumnDataType.truncated;
    return this._create<TSource>(caption, binding, opt);
  }
}
